import {
  formatNumber,
  formatPercentage,
} from "@repo/components/src/utils/numbers";
import React, { useMemo } from "react";
import PostDetailModal from "../shared/postModal";
import { PostDetailView } from "../shared/postModal/types";
import { DeliverableItem } from "./deliverables";
import { isScreenshotReportedDeliverable } from "./helpers";

const SELF_REPORTED_TTL_MS = 24 * 60 * 60 * 1000;

type Props = {
  item: DeliverableItem;
  // Off on the public share route (unauthenticated) — the embedded comments
  // analyzer never fires its query there. Set by the caller (organic/index.tsx
  // openDeliverable), which knows the route via the presence of `brandId`.
  disableComments?: boolean;
  // Absent on the share route; gates the comments export.
  brandId?: string;
};

// Thin adapter: maps a campaign-report DeliverableItem into the normalized
// PostDetailView the shared modal renders. All layout lives in
// `../shared/postModal`.
const DeliverableModal = React.memo(
  ({ item, disableComments, brandId }: Props) => {
    const { workOrder, detail } = item;

    const isSelfReported = isScreenshotReportedDeliverable(detail);
    const selfReportedExpired = useMemo(() => {
      if (!isSelfReported || !detail.publishedAt) return false;
      const ts = new Date(detail.publishedAt).getTime();
      if (!Number.isFinite(ts)) return false;
      return Date.now() - ts > SELF_REPORTED_TTL_MS;
    }, [isSelfReported, detail.publishedAt]);

    // Self-reported deliverables report replies and link taps instead of
    // comments and saves. Engagement is summed from those four interactions
    // rather than reusing `detail.interactions`, which is built from the feed
    // metric set (comments, likes, shares, saved, favorites) and so misses
    // clicks and double-counts replies through the comments bucket.
    const selfReportedInteractions =
      detail.likes + detail.replyCount + detail.shares + detail.linkClicks;
    const engagementRate =
      detail.reach > 0 ? selfReportedInteractions / detail.reach : 0;

    const view: PostDetailView = useMemo(() => {
      const summary = detail.commentsSummary;
      return {
        author: {
          name: workOrder.fullName,
          avatarUrl: workOrder.avatar?.url,
        },
        platformCode: detail.platformCode,
        postName: detail.postName,
        publishedAt: detail.publishedAt ?? detail.approvedAt,
        stats: isSelfReported
          ? [
              { label: "Views", value: formatNumber(detail.views) },
              { label: "Reach", value: formatNumber(detail.reach) },
              { label: "Likes", value: formatNumber(detail.likes) },
              { label: "Replies", value: formatNumber(detail.replyCount) },
              { label: "Shares", value: formatNumber(detail.shares) },
              { label: "Clicks", value: formatNumber(detail.linkClicks) },
              { label: "Engagement", value: formatPercentage(engagementRate) },
            ]
          : [
              { label: "Views", value: formatNumber(detail.views) },
              { label: "Reach", value: formatNumber(detail.reach) },
              { label: "Likes", value: formatNumber(detail.likes) },
              { label: "Comentarios", value: formatNumber(detail.comments) },
              { label: "Shares", value: formatNumber(detail.shares) },
              { label: "Saves", value: formatNumber(detail.saves) },
            ],
        sentiment: summary
          ? {
              distribution: summary.distribution,
              totalCount: summary.totalCount,
            }
          : null,
        contentUrl: detail.contentFile?.url,
        thumbnailUrl: detail.thumbnail?.url,
        postUrl: detail.postUrl,
        hasPermalink: !detail.isScreenshotReported,
        campaignName: null,
        commentsFilter:
          disableComments || isSelfReported || !detail.postId
            ? null
            : { postId: detail.postId, brandId },
        selfReported: isSelfReported ? { expired: selfReportedExpired } : null,
      };
    }, [
      detail,
      workOrder,
      isSelfReported,
      selfReportedExpired,
      engagementRate,
      disableComments,
      brandId,
    ]);

    return <PostDetailModal view={view} />;
  },
);

DeliverableModal.displayName = "DeliverableModal";

export default DeliverableModal;
